import type { ServiceCatalogResponse } from './assistant-settings-contracts'
import type { AssistantSettingsRepository } from './assistant-settings-repository'
import type {
  BookingService,
  ServiceBookingSettings,
} from './provider-resource-contracts'
import type { ProviderResourceRepository } from './provider-resource-repository'

/**
 * The service as Services showed it, by stable identity: the catalog record's
 * ID and the booking variant's internal key. A label or a variant group is
 * never an identity, so removing one variant leaves its siblings and their
 * group exactly as they were.
 */
export type ServiceRemovalTarget = {
  informationalServiceId?: string
  serviceIntent?: string
}

export type ServiceRemovalResult = {
  savedCatalog: ServiceCatalogResponse
  savedBooking: ServiceBookingSettings | undefined
  bookingRemoved: boolean
  catalogRemoved: boolean
}

export class ServiceRemovalError extends Error {
  readonly reason:
    | 'IDENTITY_REQUIRED'
    | 'ASSOCIATION_CHANGED'
    | 'BOOKING_AUTHORITY_UNAVAILABLE'
    | 'LAST_BOOKABLE_SERVICE'
    | 'LAST_AI_SERVICE'
  constructor(reason: ServiceRemovalError['reason'], message: string) {
    super(message)
    this.name = 'ServiceRemovalError'
    this.reason = reason
  }
}

const BOOKING_EDITABLE_STATUS = new Set(['AVAILABLE', 'REVIEW_REQUIRED'])

function associationChanged() {
  return new ServiceRemovalError(
    'ASSOCIATION_CHANGED',
    'This service changed since Services was loaded.'
  )
}

/**
 * Removes one clinic service from Flio through the two existing Settings
 * owners. Neither needs a delete verb: each already takes the whole desired
 * collection under a compare-and-set, so a removal is the same reviewed write
 * as any other edit (the mapping keeps its CANDIDATE-to-ACTIVE review and
 * policy derivation).
 *
 * The owners are separate, so this is ordered, idempotent and recoverable
 * rather than atomic:
 *  - Both owners are re-read first, and the compare-and-set values sent are
 *    theirs, never a form's. If anything else now links to this catalog
 *    record, or its own link changed, nothing is written.
 *  - The booking variant is removed before the catalog record. If the second
 *    write fails, an informational-only service remains, which the product
 *    already models; the other order would leave a bookable variant pointing
 *    at a deleted record.
 *  - A step whose record is already gone is skipped, so a retry finishes the
 *    remainder and a partial result is never reported as success.
 *
 * Both writes are Flio Settings. No NexHealth appointment type, appointment,
 * provider resource or patient record is touched.
 */
export async function removeClinicService(
  tenantId: string,
  target: ServiceRemovalTarget,
  assistant: AssistantSettingsRepository,
  provider?: ProviderResourceRepository
): Promise<ServiceRemovalResult> {
  if (!target.informationalServiceId && !target.serviceIntent) {
    throw new ServiceRemovalError(
      'IDENTITY_REQUIRED',
      'A service identity is required.'
    )
  }

  const [catalog, booking] = await Promise.all([
    assistant.getServiceCatalog(tenantId),
    provider?.getServiceBooking
      ? provider.getServiceBooking(tenantId)
      : Promise.resolve(undefined),
  ])
  if (target.serviceIntent && !booking?.configuration) {
    throw new ServiceRemovalError(
      'BOOKING_AUTHORITY_UNAVAILABLE',
      'Booking settings are unavailable right now.'
    )
  }

  // Every variant that is this service or links to its catalog record. It
  // must be exactly what the page showed: at most the one variant, linked the
  // same way (both linked to this record, or both unlinked).
  const mappings: BookingService[] = booking?.configuration?.services ?? []
  const own = mappings.filter(
    (service) =>
      Boolean(target.serviceIntent) &&
      service.serviceIntent === target.serviceIntent
  )
  const others = mappings.filter(
    (service) =>
      service.serviceIntent !== target.serviceIntent &&
      Boolean(target.informationalServiceId) &&
      service.informationalServiceId === target.informationalServiceId
  )
  if (others.length > 0 || own.length > 1) throw associationChanged()
  const [variant] = own
  if (
    variant &&
    variant.informationalServiceId !== target.informationalServiceId
  )
    throw associationChanged()

  // Two states the platform cannot hold yet, checked on the owners' current
  // data: an empty booking mapping (the database drops an empty list and the
  // mapping record then no longer decodes, for Settings and Voice alike), and
  // a catalog with no service on for AI answers (Voice cannot be built).
  if (variant && mappings.length === 1) {
    throw new ServiceRemovalError(
      'LAST_BOOKABLE_SERVICE',
      'This is the only service Flio books.'
    )
  }
  const record = target.informationalServiceId
    ? catalog.services.find(
        (service) => service.serviceId === target.informationalServiceId
      )
    : undefined
  if (
    record?.availability &&
    !catalog.services.some(
      (service) =>
        service.serviceId !== record.serviceId && service.availability
    )
  ) {
    throw new ServiceRemovalError(
      'LAST_AI_SERVICE',
      'This is the only service with AI answers on.'
    )
  }

  let savedBooking = booking
  let bookingRemoved = false
  if (variant && booking?.configuration) {
    if (
      !BOOKING_EDITABLE_STATUS.has(booking.status) ||
      !provider?.updateServiceBooking
    ) {
      throw new ServiceRemovalError(
        'BOOKING_AUTHORITY_UNAVAILABLE',
        'Booking settings are unavailable right now.'
      )
    }
    savedBooking = await provider.updateServiceBooking(tenantId, {
      expectedMappingRevision: booking.configuration.mappingRevision,
      expectedMappingFingerprint: booking.configuration.mappingFingerprint,
      services: mappings.filter(
        (service) => service.serviceIntent !== target.serviceIntent
      ),
    })
    bookingRemoved = true
  }

  let savedCatalog = catalog
  let catalogRemoved = false
  if (record) {
    savedCatalog = await assistant.updateServiceCatalog(tenantId, {
      expectedFingerprint: catalog.catalogFingerprint,
      services: catalog.services.filter(
        (service) => service.serviceId !== target.informationalServiceId
      ),
    })
    catalogRemoved = true
  }

  return { savedCatalog, savedBooking, bookingRemoved, catalogRemoved }
}
